import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  markAllNotificationsRead,
  markNotificationRead,
  subscribeToCompanyNotifications,
} from "../utils/messagingService";
import { APP_ROUTE_PATHS, buildMessageConversationRoute } from "../appRoutePaths";
import "./NotificationBell.css";

const formatNotificationTime = (notification) => {
  const millis = notification.createdAt?.toMillis?.();
  const date = millis ? new Date(millis) : new Date(notification.createdAt || 0);
  if (Number.isNaN(date.getTime())) return "New";
  return date.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const NotificationBell = ({ user, accountProfile, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState("");
  const wrapRef = useRef(null);
  const companyId = accountProfile?.companyId || accountProfile?.companyOwnerUid || "";
  const unread = useMemo(() => notifications.filter((item) => !item.read), [notifications]);

  useEffect(() => {
    if (!companyId || !user?.uid) {
      setNotifications([]);
      return undefined;
    }
    return subscribeToCompanyNotifications({
      companyId,
      userId: user.uid,
      onData: setNotifications,
      onError: (notificationError) => {
        console.warn("Notification subscription failed:", notificationError);
        setError("Notifications could not load.");
      },
    });
  }, [companyId, user?.uid]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.key === "Escape" || (event.type === "pointerdown" && !wrapRef.current?.contains(event.target))) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const openNotification = async (notification) => {
    if (companyId && notification.id && !notification.read) {
      setNotifications((current) => current.map((item) => item.id === notification.id ? { ...item, read: true } : item));
      await markNotificationRead(companyId, notification.id).catch(() => {});
    }
    setOpen(false);
  };

  const markAllRead = async () => {
    try {
      await markAllNotificationsRead(companyId, notifications);
      setNotifications((current) => current.map((item) => ({ ...item, read: true })));
    } catch (actionError) {
      setError("Could not mark notifications read.");
    }
  };

  if (!companyId || !user?.uid) {
    return compact ? null : (
      <Link className="notification-bell disabled" to={APP_ROUTE_PATHS.notifications} title="Company notifications">
        Bell
      </Link>
    );
  }

  return (
    <div ref={wrapRef} className={`notification-bell-wrap${open ? " open" : ""}`}>
      <button
        type="button"
        className={`notification-bell${unread.length ? " has-unread" : ""}`}
        onClick={() => setOpen((current) => !current)}
        title="Notifications"
      >
        Bell
        {unread.length > 0 && <span>{unread.length > 99 ? "99+" : unread.length}</span>}
      </button>
      {open && (
        <section className="notification-popover">
          <header>
            <div>
              <span>Notifications</span>
              <strong>{unread.length ? `${unread.length} unread` : "All caught up"}</strong>
            </div>
            <button type="button" onClick={markAllRead} disabled={!unread.length}>Mark all read</button>
          </header>
          {error && <p className="notification-error">{error}</p>}
          <div className="notification-list">
            {notifications.length ? notifications.slice(0, 8).map((notification) => (
              <Link
                key={notification.id}
                to={
                  notification.contextRoute ||
                  buildMessageConversationRoute(notification.conversationId || "")
                }
                className={notification.read ? "" : "unread"}
                onClick={() => openNotification(notification)}
              >
                <b>{notification.type === "mention" ? "Mention" : notification.title || "Message"}</b>
                <strong>{notification.conversationTitle || notification.contextLabel || "Workspace update"}</strong>
                <span>{notification.preview || "Open conversation"}</span>
                <small>{formatNotificationTime(notification)}</small>
              </Link>
            )) : <p>No notifications yet.</p>}
          </div>
          <footer>
            <Link to={APP_ROUTE_PATHS.notifications} onClick={() => setOpen(false)}>View all notifications</Link>
            <Link to={APP_ROUTE_PATHS.messages} onClick={() => setOpen(false)}>Messages</Link>
          </footer>
        </section>
      )}
    </div>
  );
};

export default NotificationBell;
